import api from '@/api/client';
import { notify } from '@/core/toast/notify';
import {
  isPublicStoredAssetPath,
  normalizeStoredPath,
  resolveFileContentRequestPath,
} from '@/utils/fileUrl';
import { downloadBlob } from '@/utils/downloadBlob';

/**
 * Fetches a stored file with the session client and opens it in a new tab for inline viewing.
 */
export async function openStoredFileInNewTab(storedPath: string | undefined | null): Promise<void> {
  const trimmed = storedPath?.trim();
  if (!trimmed) {
    notify.error('Document is not available.');
    return;
  }

  // Open window synchronously in click handler without 'noopener' so script retains window handle
  const popup = window.open('about:blank', '_blank');

  if (popup && popup.document) {
    try {
      popup.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Opening Document...</title>
            <style>
              body { font-family: system-ui, -apple-system, sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; margin: 0; background: #0f172a; color: #f8fafc; }
              .loader { border: 3px solid #334155; border-top: 3px solid #3b82f6; border-radius: 50%; width: 28px; height: 28px; animation: spin 0.8s linear infinite; margin: 0 auto 16px; }
              @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            </style>
          </head>
          <body>
            <div style="text-align:center;">
              <div class="loader"></div>
              <p style="font-size:14px;font-weight:500;">Loading document...</p>
            </div>
          </body>
        </html>
      `);
    } catch {
      // Ignore if document.write is disallowed
    }
  }

  try {
    let blob: Blob;
    const authPath = resolveFileContentRequestPath(trimmed, 'inline');
    if (authPath && !isPublicStoredAssetPath(trimmed)) {
      const response = await api.get(authPath, { responseType: 'blob' });
      blob = response.data as Blob;
    } else {
      const staticPath = normalizeStoredPath(trimmed);
      if (!staticPath) {
        throw new Error('unavailable');
      }
      const response = await api.get(staticPath, { responseType: 'blob' });
      blob = response.data as Blob;
    }

    const filename = trimmed.split('/').pop()?.split('?')[0] || 'document';
    const blobUrl = URL.createObjectURL(blob);

    if (popup && !popup.closed) {
      popup.location.href = blobUrl;
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
    } else {
      downloadBlob(blob, filename);
    }
  } catch {
    if (popup && !popup.closed) {
      popup.close();
    }
    notify.error('Could not open document. Try again or download the file.');
  }
}
